import './Page.css'
import './pdfjs_viewer.css'
import React, { useState, useRef, useEffect } from 'react'
import { useSetState } from 'react-use'
import classnames from 'classnames'
import uuid from 'uuid'
import fromPairs from 'lodash/fromPairs'
import animateScrollTo from 'animated-scroll-to'
import throttle from 'lodash/throttle'
import formatMessage from 'format-message'
import PDFCanvas from './PDFCanvas'
import TextLayer from './TextLayer'
import PDFAnnotationLayer from './PDFAnnotationLayer'
import PageAnnotations from '../annotations/PageAnnotations'
import Draw from '../draw/Draw'
import VisibilityObserver from '../VisibilityObserver'
import CommentIndicator from './../comments/CommentIndicator'
import ArrowDown from '../icons/ArrowDown'
import ArrowUp from '../icons/ArrowUp'
import { PAGE_BORDER_WIDTH } from '../constants'
import { getPageWidthAfterScaling, splitRootAnnotationsAndReplies, compareRectY } from '../utils'

const isMSEdge = /Edge/.test(navigator.userAgent)

const getPdfViewport = (pdfPage, viewportScale, rotation) => {
  return typeof rotation === 'number'
    ? pdfPage.getViewport({ scale: viewportScale, rotation })
    : pdfPage.getViewport({ scale: viewportScale })
}

class Page_ extends React.Component {
  constructor(props) {
    super(props)

    this.handleManageScrollTop__throttled__wrapper = (newScrollTop) => {
      const hasFinishedScrolling = newScrollTop !== this.props.stagedScrollTop
      if (hasFinishedScrolling === false || newScrollTop === undefined) {
        return
      }
      this.props.setStagedScrollTop(newScrollTop)
      this.props.setState({
        isAutomaticallyScrollingComments: true,
      })
      this.handleManageScrollTop__throttled(newScrollTop)
    }

    this.handleManageScrollTop__throttled = throttle(this.handleManageScrollTop, 100, {
      leading: false,
      trailing: true,
    })

    this.handleScrollTopEvent_throttled = throttle(this.handleScrollTopEvent, 100, {
      leading: false,
      trailing: true,
    })
  }

  handleCommentGroupHeightsChange = (commentGroupHeights) => {
    this.props.setState({ commentGroupHeights })
    this.updateNumberOfCommentsOutOfViewAndCheckIntersection()
  }

  handleScrollTopEvent_throttled_wrapper = (event) => {
    event.persist()
    this.handleScrollTopEvent_throttled(event)
  }

  componentWillReceiveProps(nextProps) {
    if (
      typeof this.props.pdfPage === 'object' &&
      nextProps.viewportScale !== this.props.viewportScale
    ) {
      if (typeof this.props.pdfPage === 'undefined') {
        return
      }
      this.props.setPageViewport(
        this.props.pdfPage !== null && typeof this.props.pdfPage === 'object'
          ? getPdfViewport(
              this.props.pdfPage,
              nextProps.viewportScale,
              this.props.customPageRotation,
            )
          : this.props.pageViewport,
      )
    } else if (nextProps.customPageRotation !== this.props.customPageRotation) {
      this.props.setPageViewport(
        getPdfViewport(this.props.pdfPage, this.props.viewportScale, nextProps.customPageRotation),
      )
    }
  }

  componentDidMount() {
    if (this.props.currentPageNum === this.props.pageNumber + 1) {
      this.props.onPageResize()
    }
    setTimeout(this.updateNumberOfCommentsOutOfViewAndCheckIntersection, 1000)
    setTimeout(this.updateNumberOfCommentsOutOfViewAndCheckIntersection, 1500)
  }

  componentDidUpdate(prevProps, _prevState) {
    const isNowVisible = prevProps.isVisible === false && this.props.isVisible === true

    if (isNowVisible) {
      setTimeout(this.updateNumberOfCommentsOutOfViewAndCheckIntersection, 750)
      setTimeout(this.updateNumberOfCommentsOutOfViewAndCheckIntersection, 1500)
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const newAnnotations = nextProps.annotations.filter(
      (nextPropAnnotation) =>
        this.props.annotations.some((annotation) => annotation.id === nextPropAnnotation.id) ===
        false,
    )

    if (newAnnotations.length > 0) {
      this.props.setCommentContainerRefs({
        ...this.props.commentContainerRefs,
        ...fromPairs(newAnnotations.map((annotation) => [annotation.id, React.createRef()])),
      })
    }
  }

  handleCreateAnnotation = (annotation) => {
    const viewport = getPdfViewport(
      this.props.pdfPage,
      this.props.viewportScale,
      this.props.customPageRotation,
    )
    const newAnnotation = {
      ...annotation,
      id: typeof annotation.id === 'string' ? annotation.id : uuid.v4(),
      page: this.props.pageNumber,
      rotation: viewport.rotation,
    }
    // per RD-4963, don't create  highlight and strikeout annotations that
    // missed the mousedown start
    if (
      (newAnnotation.type === 'highlight' || newAnnotation.type === 'strikeout') &&
      Math.abs(newAnnotation.rect[0][0] - newAnnotation.rect[1][0]) < 1
    ) {
      return
    }
    const viewportHeight = this.props.pageViewport
      ? this.props.pageViewport.height
      : this.props.viewportHeight
    if (
      newAnnotation.type === 'ink' &&
      newAnnotation.inklist &&
      newAnnotation.inklist.gestures &&
      newAnnotation.inklist.gestures.length > 1
    ) {
      this.props.onUpdateAnnotation(
        viewportHeight,
        this.props.viewportScale,
        {
          ...newAnnotation,
          user_name: this.props.sessionUserObject.user_name,
          user_id: this.props.sessionUserObject.user_id,
        },
        Boolean(this.props.multiStrokeAnnotationId),
      )
    } else {
      this.props.onCreateAnnotation(
        viewportHeight,
        this.props.viewportScale,
        {
          ...newAnnotation,
          user_name: this.props.sessionUserObject.user_name,
          user_id: this.props.sessionUserObject.user_id,
        },
        Boolean(this.props.multiStrokeAnnotationId),
      )
    }
    window.getSelection().removeAllRanges()
  }

  onCreateAnnotation__addRotation = (height, scale, annotation, isStaged = false) => {
    const viewport = getPdfViewport(
      this.props.pdfPage,
      this.props.viewportScale,
      this.props.customPageRotation,
    )
    const annotationWithRotation = {
      ...annotation,
      rotation: viewport.rotation,
    }
    return this.props.onCreateAnnotation(height, scale, annotationWithRotation, isStaged)
  }

  handleVisiblityChange = (intersectionRatio) => {
    if (this.props.lastIntersectionRatio.current !== intersectionRatio) {
      const isVisible = Boolean(intersectionRatio)
      const wasVisibleSinceLastScale = this.props.wasVisibleSinceLastScale || isVisible
      this.props.setWasVisibleSinceLastScale(wasVisibleSinceLastScale)
      this.props.setIsVisible(isVisible)
      setTimeout(() => {
        // TODO: replace with useEffect
        this.props.onVisibilityChange(this.props.pageNumber, intersectionRatio)
        this.props.lastIntersectionRatio.current = intersectionRatio
      }, 0)
    }
  }

  handleRendered = () => {
    const isFirstPage = this.props.pageNumber === 0

    if (isFirstPage && window.DocViewer && this.props.isCanvasRendered === false) {
      const renderTime = Date.now() - window.DocViewer.start

      const pdfByteSize =
        this.props.pdfDocument &&
        this.props.pdfDocument.transport &&
        this.props.pdfDocument.transport._fullReader &&
        this.props.pdfDocument.transport._fullReader._loaded
      if (pdfByteSize) {
        const megabytes = pdfByteSize / 1000000
        if (megabytes >= 10) {
          const seconds = renderTime / 1000
          const secondsPerMegabyte = seconds / megabytes
        }
      }
    }

    this.props.setIsCanvasRendered(true)
  }

  setDiv = (node) => {
    this.props.setPageDiv(node)
  }

  handleManageScrollTop = (newScrollTop) => {
    animateScrollTo(newScrollTop, {
      cancelOnUserAction: false,
      element: this.props.commentsDiv.current,
      speed: 500,
      minDuration: 500,
      maxDuration: 1000,
      onComplete: () => {
        this.props.setCurrentScrollTop(this.props.commentsDiv.current.scrollTop)
        this.props.setStagedScrollTop(Number.MAX_SAFE_INTEGER)
        this.props.setState({
          isAutomaticallyScrollingComments: false,
        })
      },
    })
  }

  updateNumberOfCommentsOutOfViewAndCheckIntersection = () => {
    const annotationsAboveScrollTop = Object.keys(this.props.managedCommentGroupTops).filter(
      (annotationId) => {
        const commentGroupTop = this.props.managedCommentGroupTops[annotationId]
        const commentGroupHeight = this.props.state.commentGroupHeights[annotationId]
        return this.props.currentScrollTop > commentGroupTop + commentGroupHeight
      },
    )

    const pageHeight = this.props.pageViewport ? this.props.pageViewport.height : 0
    const commentsDivHeight = pageHeight + PAGE_BORDER_WIDTH * 2

    const annotationBelowScrollBottom = Object.keys(this.props.managedCommentGroupTops).filter(
      (annotationId) => {
        const commentGroupTop = this.props.managedCommentGroupTops[annotationId]
        return this.props.currentScrollTop + commentsDivHeight < commentGroupTop
      },
    )

    const annotationsIntersectingWithScrollTop = Object.keys(
      this.props.managedCommentGroupTops,
    ).filter((annotationId) => {
      const commentGroupHeight = this.props.state.commentGroupHeights[annotationId]
      const commentGroupTop = this.props.managedCommentGroupTops[annotationId]
      return (
        this.props.currentScrollTop > commentGroupTop &&
        this.props.currentScrollTop < commentGroupTop + commentGroupHeight
      )
    })
    const annotationsIntersectingWithScrollBottom = Object.keys(
      this.props.managedCommentGroupTops,
    ).filter((annotationId) => {
      const commentGroupTop = this.props.managedCommentGroupTops[annotationId]
      const commentGroupHeight = this.props.state.commentGroupHeights[annotationId]
      return (
        this.props.currentScrollTop + commentsDivHeight > commentGroupTop &&
        this.props.currentScrollTop + commentsDivHeight < commentGroupTop + commentGroupHeight
      )
    })

    this.props.setNumberOfCommentsBelowView(annotationBelowScrollBottom.length)
    this.props.setNumberOfCommentsAboveView(annotationsAboveScrollTop.length)
    this.props.setIsCommentIntersectingWithTop(annotationsIntersectingWithScrollTop.length > 0)
    this.props.setState({
      isCommentIntersectingWithBottom: annotationsIntersectingWithScrollBottom.length > 0,
    })
  }

  handleScrollTopEvent = (event) => {
    if (event.target) {
      this.props.setCurrentScrollTop(event.target.scrollTop)
      this.updateNumberOfCommentsOutOfViewAndCheckIntersection()
    } else {
      console.warn('event.target does not exist')
    }
  }

  render() {
    const pageWidth = this.props.pageViewport ? this.props.pageViewport.width : 0

    const pageHeight = this.props.pageViewport ? this.props.pageViewport.height : 0

    const isPageInRange =
      this.props.pageNumber >= this.props.currentPageNum - 4 &&
      this.props.pageNumber <= this.props.currentPageNum + 4

    const shouldBeRendered =
      this.props.isVisible || this.props.wasVisibleSinceLastScale || isPageInRange

    const truncatedPageWidth = Math.trunc(pageWidth)

    const [_all_replies, stagedRootAnnotations] = this.props.annotations.reduce(
      splitRootAnnotationsAndReplies,
      [[], []],
    )

    const commentPortals = stagedRootAnnotations
      .slice()
      .sort(compareRectY)
      .map((annotation) => {
        return (
          <div
            key={annotation.id}
            ref={this.props.commentContainerRefs[annotation.id]}
            id={`Annotation-${annotation.id}-comment-group`}
          />
        )
      })

    const pageWidthWithComments = getPageWidthAfterScaling(
      this.props.displayCommentRegion ||
        this.props.controlMode !== 'selection' ||
        this.props.documentHasAnnotations,
      pageWidth,
    )
    const pageContainerClassNames = classnames('Page-container', {
      'Pen-is-annotating':
        (isMSEdge && this.props.controlMode !== 'selection') || this.props.isPenAnnotating,
    })
    // MS Edge has problem with the className of "Page", so we supplemented
    //   with "PageComponent" ¯\_(ツ)_/¯
    return (
      <div
        className={pageContainerClassNames}
        ref={this.props.pageRef}
        style={{
          width: `${pageWidthWithComments}px`,
          borderTop: this.props.pageNumber !== 0 ? '1px dashed #A8A8A8' : '', // TODO: dynamic classes: classname()
        }}
      >
        <div
          className={classnames('Page', 'PageComponent', {
            'Page--not-rendered': this.props.isCanvasRendered === false,
            'selection-disabled': this.props.isTextSelectionEnabled === false,
            'is-multistroke-drawing': Boolean(this.props.multiStrokeAnnotationId),
          })}
          id={`page-${this.props.pageNumber}`}
          ref={this.setDiv}
          style={{
            height: `${pageHeight + PAGE_BORDER_WIDTH * 2}px`,
            width: `${truncatedPageWidth + PAGE_BORDER_WIDTH * 2}px`,
          }}
          tabIndex={-1}
        >
          {this.props.pageDiv instanceof HTMLElement && (
            <VisibilityObserver
              onVisibilityChange={this.handleVisiblityChange}
              pageDiv={this.props.pageDiv}
              pageNumber={this.props.pageNumber}
            />
          )}

          <PDFCanvas
            shouldBeRendered={shouldBeRendered}
            page={this.props.pdfPage}
            viewport={this.props.pageViewport}
            viewportHeight={pageHeight}
            viewportWidth={pageWidth}
            onRendered={this.handleRendered}
            customPageRotation={this.props.customPageRotation}
          />

          {this.props.pdfPage && this.props.textContent && (
            <Draw
              currentBgColor={this.props.currentBgColor}
              currentColor={this.props.currentColor}
              currentFont={this.props.currentFont}
              height={pageHeight}
              isHoveringOverAnnotation={Boolean(this.props.hoveredAnnotationId)}
              multiStrokeAnnotationId={this.props.multiStrokeAnnotationId}
              onChange={this.handleCreateAnnotation}
              onDrawingEnd={this.props.handleDrawingEnd}
              onDrawingStart={this.props.handleDrawingStart}
              onMultiStrokeAnnotation={this.props.handleMultiStrokeAnnotation}
              onBlockContextMenu={this.props.handleContextMenuBlock}
              onIsPenAnnotatingChange={this.props.onIsPenAnnotatingChange}
              shouldBeRendered={shouldBeRendered}
              shouldBlockContextMenu={this.props.shouldBlockContextMenu}
              strokeWidth={this.props.strokeWidth}
              style={{
                position: 'absolute',
                height: `${pageHeight}px`,
                width: `${pageWidth}px`,
              }}
              type={this.props.controlMode}
              viewportHeight={pageHeight}
              viewportScale={this.props.viewportScale}
              viewportWidth={pageWidth}
              width={pageWidth}
              pageNumber={this.props.pageNumber}
            >
              {/* Keep rendered so user can search text in browser */}
              <TextLayer
                pageNumber={this.props.pageNumber}
                textContent={this.props.textContent}
                viewport={this.props.pageViewport}
                viewportHeight={pageHeight}
                viewportWidth={pageWidth}
              />
              {shouldBeRendered && this.props.isCanvasRendered && this.props.pdfPage !== null && (
                <PDFAnnotationLayer
                  pdfPage={this.props.pdfPage}
                  viewport={this.props.pageViewport}
                  viewportWidth={pageWidth}
                  viewportScale={this.props.viewportScale}
                />
              )}
              {shouldBeRendered && this.props.isCanvasRendered && (
                <PageAnnotations
                  annotations={this.props.annotations}
                  changePagesContainerScrollTop={this.props.changePagesContainerScrollTop}
                  changeScrollTop={this.handleManageScrollTop__throttled__wrapper}
                  commentContainerRefs={this.props.commentContainerRefs}
                  commentContainerBottomOffsetRef={
                    this.props.commentContainerBottomOffsetRef.current
                  }
                  controlMode={this.props.controlMode}
                  currentScrollTop={this.props.currentScrollTop}
                  handlePreviousPageFocus={this.props.handlePreviousPageFocus}
                  handleNextPageFocus={this.props.handleNextPageFocus}
                  hoveredAnnotationId={this.props.hoveredAnnotationId}
                  isAnnotatable={this.props.isAnnotatable}
                  isCommenting={this.props.isCommenting}
                  isDrawing={this.props.isDrawing}
                  isAutomaticallyScrollingComments={
                    this.props.state.isAutomaticallyScrollingComments
                  }
                  isShiftPressed={this.props.isShiftPressed}
                  isVisible={this.props.isVisible}
                  multiStrokeAnnotationId={this.props.multiStrokeAnnotationId}
                  onCreateAnnotation={this.onCreateAnnotation__addRotation}
                  onCommentGroupHeightsChange={this.handleCommentGroupHeightsChange}
                  onCurrentBgColorChange={this.props.onCurrentBgColorChange}
                  onCurrentColorChange={this.props.onCurrentColorChange}
                  onCurrentInkStrokeWidthChange={this.props.onCurrentInkStrokeWidthChange}
                  onCurrentTextSizeChange={this.props.onCurrentTextSizeChange}
                  onDeleteAnnotation={this.props.onDeleteAnnotation}
                  onDeselectAnnotation={this.props.onDeselectAnnotation}
                  onDisableTextSelection={this.props.disableTextSelection}
                  onEnableTextSelection={this.props.enableTextSelection}
                  onHoverAnnotation={this.props.onHoverAnnotation}
                  onIsCommentingChange={this.props.onIsCommentingChange}
                  onIsPenAnnotatingChange={this.props.onIsPenAnnotatingChange}
                  onManagedCommentGroupTopsChange={this.props.handleManagedCommentGroupTopsChange}
                  onMultiStrokeAnnotation={this.props.handleMultiStrokeAnnotation}
                  onSelectAnnotation={this.props.onSelectAnnotation}
                  onBlockContextMenu={this.handleContextMenuBlock}
                  onUpdateAnnotation={this.props.onUpdateAnnotation}
                  pageHeight={pageHeight}
                  pageNumber={this.props.pageNumber}
                  selectedAnnotationId={this.props.selectedAnnotationId}
                  sessionUserObject={this.props.sessionUserObject}
                  viewportScale={this.props.viewportScale}
                  viewportHeight={pageHeight}
                  viewportWidth={pageWidth}
                />
              )}
            </Draw>
          )}
          {this.props.state.someCommentsAboveThreshhold && (
            <button
              className="Page-show-previous-comments-button"
              onClick={this.handlePreviousCommentButtonClick}
            >
              {formatMessage('See previous comments for page {pageNumber}', {
                pageNumber: this.props.pageNumber + 1,
              })}
            </button>
          )}
        </div>

        <div style={{ overflow: 'hidden' }}>
          <div style={{ position: 'relative' }}>
            <CommentIndicator numberOfCommentsOutOfView={this.props.numberOfCommentsAboveView}>
              <ArrowUp />
            </CommentIndicator>
          </div>
          <div className="Page-comment-line-top-margin" />
          {(this.props.isCommentIntersectingWithTop ||
            this.props.numberOfCommentsAboveView > 0) && <div className="Page-comment-line" />}

          <div
            className="PageContainerComments"
            onScroll={this.handleScrollTopEvent_throttled_wrapper}
            ref={this.props.commentsDiv}
            style={{
              height: `${pageHeight + PAGE_BORDER_WIDTH * 2}px`,
            }}
          >
            {commentPortals}
            <div ref={this.props.commentContainerBottomOffsetRef.current} />
          </div>
          {(this.props.state.isCommentIntersectingWithBottom ||
            this.props.numberOfCommentsBelowView > 0) && <div className="Page-comment-line" />}

          <div style={{ position: 'relative' }}>
            <CommentIndicator numberOfCommentsOutOfView={this.props.numberOfCommentsBelowView}>
              <ArrowDown />
            </CommentIndicator>
          </div>
        </div>
      </div>
    )
  }
}

export default function Page(props) {
  const lastIntersectionRatio = useRef(null)
  const commentContainerBottomOffsetRef = useRef(React.createRef())
  const commentsDiv = useRef(null)

  const [currentScrollTop, setCurrentScrollTop] = useState(0)
  const [wasVisibleSinceLastScale, setWasVisibleSinceLastScale] = useState(false)
  const [textContent, setTextContent] = useState(null)
  const [shouldBlockContextMenu, setShouldBlockContextMenu] = useState(false)
  const [pdfPage, setPdfPage] = useState(null)
  const [pageViewport, setPageViewport] = useState(null)
  const [pageDiv, setPageDiv] = useState(null)
  const [numberOfCommentsBelowView, setNumberOfCommentsBelowView] = useState(0)
  const [numberOfCommentsAboveView, setNumberOfCommentsAboveView] = useState(0)
  const [multiStrokeAnnotationId, setMultiStrokeAnnotationId] = useState(null)
  const [isDrawing, setIsDrawing] = useState(false)
  const [isCanvasRendered, setIsCanvasRendered] = useState(false)
  const [isTextSelectionEnabled, setIsTextSelectionEnabled] = useState(true)
  const [managedCommentGroupTops, setManagedCommentGroupTops] = useState({})
  const [stagedScrollTop, setStagedScrollTop] = useState(Number.MAX_SAFE_INTEGER)
  const [isVisible, setIsVisible] = useState(props.pageNumber === 0)
  const [isCommentIntersectingWithTop, setIsCommentIntersectingWithTop] = useState(false)
  const [commentContainerRefs, setCommentContainerRefs] = useState(
    fromPairs(props.annotations.map((annotation) => [annotation.id, React.createRef()])),
  )
  const [state, setState] = useSetState({
    commentGroupHeights: {},
    isCommentIntersectingWithBottom: false,
    isAutomaticallyScrollingComments: false,
  })

  useEffect(() => {
    if (!pdfPage || !pageViewport) {
      return
    }
    pdfPage
      .getTextContent({ normalizeWhitespace: true })
      .then((textContent) => {
        setTextContent(textContent)
      })
      .catch((error) => {
        console.error('GET_TEXT_CONTENT_ERROR', error)
      })
  }, [pageViewport])

  const handleManagedCommentGroupTopsChange = (managedCommentGroupTops) => {
    setManagedCommentGroupTops(managedCommentGroupTops)

    // Auto-scroll to show comments that would be cut off at the bottom
    setTimeout(() => {
      if (!commentsDiv.current || !pageViewport) return

      const containerHeight = pageViewport.height + PAGE_BORDER_WIDTH * 2

      // Check each comment group to see if it's near the bottom
      Object.entries(managedCommentGroupTops).forEach(([annotationId, top]) => {
        const commentHeight = state.commentGroupHeights[annotationId] || 0
        const commentBottom = top + commentHeight

        // If comment is in the lower portion of the viewport and extends beyond visible area
        if (top > containerHeight * 0.7 && commentBottom > containerHeight - 50) {
          // Scroll to show the bottom of the comment
          const scrollTarget = Math.min(
            commentBottom - containerHeight + 100,
            commentsDiv.current.scrollHeight,
          )
          if (commentsDiv.current.scrollTop < scrollTarget) {
            commentsDiv.current.scrollTop = scrollTarget
            setCurrentScrollTop(scrollTarget)
          }
        }
      })
    }, 100)
  }

  const handleDrawingStart = () => {
    props.onSelectAnnotation(null)
    props.onHoverAnnotation(null)
    setIsDrawing(true)
  }

  const handlePreviousPageFocus = () => {
    props.focusOnPreviousPage(props.pageRef)
  }

  const enableTextSelection = () => {
    setIsTextSelectionEnabled(true)
  }

  const handleMultiStrokeAnnotation = (multiStrokeAnnotationId) => {
    setMultiStrokeAnnotationId(multiStrokeAnnotationId)
  }

  const handleDrawingEnd = () => {
    setIsDrawing(false)
  }

  useEffect(() => {
    if (!pdfPage && props.pdfDocument) {
      props.pdfDocument
        .getPage(props.pageNumber + 1)
        .then((pdfPage) => {
          const pageViewport = getPdfViewport(
            pdfPage,
            props.viewportScale,
            props.customPageRotation,
          )
          setPdfPage(pdfPage)
          setPageViewport(pageViewport)
        })
        .catch((error) => {
          console.log('GET_PAGE_ERROR', error)
        })
    }
  }, [])

  const disableTextSelection = () => {
    setIsTextSelectionEnabled(false)
  }

  useEffect(() => {
    const isFirstPage = props.pageNumber === 0
    lastIntersectionRatio.current = isFirstPage ? 0.9 : 0 // the amount of the page visible
  }, [props.pageNumber, textContent])

  useEffect(() => {
    if (wasVisibleSinceLastScale) {
      setWasVisibleSinceLastScale(false)
    }
  }, [props.viewportHeight, props.viewportWidth])

  const handleNextPageFocus = () => {
    props.focusOnNextPage(props.pageRef)
  }

  const handleContextMenuBlock = (shouldBlock) => {
    setShouldBlockContextMenu(shouldBlock)
  }

  return (
    <Page_
      {...props}
      currentScrollTop={currentScrollTop}
      setCurrentScrollTop={setCurrentScrollTop}
      wasVisibleSinceLastScale={wasVisibleSinceLastScale}
      setWasVisibleSinceLastScale={setWasVisibleSinceLastScale}
      textContent={textContent}
      setTextContent={setTextContent}
      shouldBlockContextMenu={shouldBlockContextMenu}
      setShouldBlockContextMenu={setShouldBlockContextMenu}
      pdfPage={pdfPage}
      setPdfPage={setPdfPage}
      pageViewport={pageViewport}
      setPageViewport={setPageViewport}
      pageDiv={pageDiv}
      setPageDiv={setPageDiv}
      numberOfCommentsBelowView={numberOfCommentsBelowView}
      setNumberOfCommentsBelowView={setNumberOfCommentsBelowView}
      numberOfCommentsAboveView={numberOfCommentsAboveView}
      setNumberOfCommentsAboveView={setNumberOfCommentsAboveView}
      multiStrokeAnnotationId={multiStrokeAnnotationId}
      setMultiStrokeAnnotationId={setMultiStrokeAnnotationId}
      isDrawing={isDrawing}
      setIsDrawing={setIsDrawing}
      isCanvasRendered={isCanvasRendered}
      setIsCanvasRendered={setIsCanvasRendered}
      isTextSelectionEnabled={isTextSelectionEnabled}
      setIsTextSelectionEnabled={setIsTextSelectionEnabled}
      managedCommentGroupTops={managedCommentGroupTops}
      setManagedCommentGroupTops={setManagedCommentGroupTops}
      stagedScrollTop={stagedScrollTop}
      setStagedScrollTop={setStagedScrollTop}
      isVisible={isVisible}
      setIsVisible={setIsVisible}
      isCommentIntersectingWithTop={isCommentIntersectingWithTop}
      setIsCommentIntersectingWithTop={setIsCommentIntersectingWithTop}
      handleManagedCommentGroupTopsChange={handleManagedCommentGroupTopsChange}
      handleDrawingStart={handleDrawingStart}
      handlePreviousPageFocus={handlePreviousPageFocus}
      enableTextSelection={enableTextSelection}
      handleMultiStrokeAnnotation={handleMultiStrokeAnnotation}
      handleDrawingEnd={handleDrawingEnd}
      commentContainerRefs={commentContainerRefs}
      setCommentContainerRefs={setCommentContainerRefs}
      disableTextSelection={disableTextSelection}
      state={state}
      setState={setState}
      lastIntersectionRatio={lastIntersectionRatio}
      commentContainerBottomOffsetRef={commentContainerBottomOffsetRef}
      handleNextPageFocus={handleNextPageFocus}
      handleContextMenuBlock={handleContextMenuBlock}
      commentsDiv={commentsDiv}
    />
  )
}
